<script setup lang="ts">
  import { UiModalHeader, ModalDocumentInfo, ModalPrintDocument } from '@/shared/ui';
  import { buildQualityRecordHeaderPrintStyleOverrides } from '@/shared/lib/print';

  /**
   * Shell of every read only modal that prints as a quality record: the modal bar,
   * the institutional header, the print row and the body from the slot. The print
   * utility takes the first child of `#modal-content` as the header repeated on
   * every printed page, so `ModalDocumentInfo` has to stay the first child.
   */
  withDefaults(
    defineProps<{
      modalTitle: string;
      formTitle: string;
      documentInfo: {
        code?: string;
        revision?: string;
        documentNumber?: string;
        documentPrefix?: string;
      };
      qualityRecordCode?: string;
      pillLabel?: string;
      compact?: boolean;
      hasDocumentPrefix?: boolean;
      printStyles?: string;
      logoUrl: string;
      resolveLogoSrc?: () => Promise<string>;
    }>(),
    {
      qualityRecordCode: undefined,
      pillLabel: undefined,
      compact: false,
      hasDocumentPrefix: true,
      printStyles: () => buildQualityRecordHeaderPrintStyleOverrides(),
      resolveLogoSrc: undefined,
    }
  );

  const emit = defineEmits<{
    close: [];
    logoError: [];
  }>();
</script>

<template>
  <div class="flex flex-col bg-white-01 rounded-2xl w-[90vw] max-h-[90vh]">
    <UiModalHeader
      class="px-6 py-4 text-sm border-b border-gray-02"
      :title="modalTitle"
      @close="emit('close')"
    />
    <div id="modal-content" class="flex flex-col p-6 overflow-y-auto">
      <ModalDocumentInfo
        :document-info="documentInfo"
        :title="formTitle"
        :quality-record-code="qualityRecordCode"
        :logo-url="logoUrl"
        @logo-error="emit('logoError')"
      />
      <ModalPrintDocument
        :document-info="documentInfo"
        :title="formTitle"
        :pill-label="pillLabel"
        :compact="compact"
        :has-document-prefix="hasDocumentPrefix"
        :print-styles="printStyles"
        :resolve-logo-src="resolveLogoSrc"
      />
      <slot />
    </div>
  </div>
</template>

<style scoped>
  @media print {
    .modal-header {
      display: none;
    }

    .print-button {
      display: none;
    }
  }
</style>
